import React, { useState, useEffect } from 'react';
import { 
  Globe, CheckCircle, XCircle, Clock, RefreshCw, Trash2, ExternalLink, 
  Copy, AlertTriangle, Sparkles, Crown, Building2, Lock, Check, HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import SimpleTooltip from '../components/SimpleTooltip';

const DomainSettings = () => {
  const [loading, setLoading] = useState(true);
  const [subdomainData, setSubdomainData] = useState(null);
  const [domainData, setDomainData] = useState(null);
  const [planFeatures, setPlanFeatures] = useState(null);
  
  // Subdomain state
  const [editingSubdomain, setEditingSubdomain] = useState(false);
  const [newSubdomain, setNewSubdomain] = useState('');
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [savingSubdomain, setSavingSubdomain] = useState(false);
  
  // Custom domain state
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    try {
      const [subdomainRes, domainRes, featuresRes] = await Promise.all([
        api.get('/tenant/subdomain'),
        api.get('/tenant/domain'),
        api.get('/tenant/plan-features')
      ]);
      setSubdomainData(subdomainRes.data);
      setDomainData(domainRes.data);
      setPlanFeatures(featuresRes.data);
      setNewSubdomain(subdomainRes.data.subdomain || '');
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load domain settings');
    } finally {
      setLoading(false);
    }
  };

  // Subdomain handlers
  const handleCheckSubdomain = async (subdomain) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainAvailable(null);
      return;
    }
    
    setCheckingSubdomain(true);
    try {
      const response = await api.get(`/tenant/subdomain/check/${subdomain}`);
      setSubdomainAvailable(response.data.available);
    } catch (error) {
      setSubdomainAvailable(false);
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const handleSubdomainChange = (value) => {
    // Only allow lowercase letters, numbers, and hyphens
    const cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setNewSubdomain(cleaned);
    setSubdomainAvailable(null);
    
    // Debounce check
    clearTimeout(window.subdomainCheckTimeout);
    window.subdomainCheckTimeout = setTimeout(() => {
      handleCheckSubdomain(cleaned);
    }, 500);
  };

  const handleSaveSubdomain = async () => {
    if (!newSubdomain || newSubdomain.length < 3) {
      toast.error('Subdomain must be at least 3 characters');
      return;
    }
    
    setSavingSubdomain(true);
    try {
      const response = await api.post('/tenant/subdomain', { subdomain: newSubdomain });
      toast.success('Subdomain updated successfully!');
      setEditingSubdomain(false);
      fetchAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save subdomain');
    } finally {
      setSavingSubdomain(false);
    }
  };

  const handleGenerateSubdomain = async () => {
    try {
      const response = await api.post('/tenant/subdomain/generate', {});
      setNewSubdomain(response.data.suggested);
      setSubdomainAvailable(true);
    } catch (error) {
      toast.error('Failed to generate subdomain');
    }
  };

  // Custom domain handlers
  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      toast.error('Please enter a domain');
      return;
    }

    setAdding(true);
    try {
      const response = await api.post('/tenant/domain', { custom_domain: newDomain });
      toast.success(response.data.message);
      setShowAddDomain(false);
      setNewDomain('');
      fetchAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const handleVerifyDomain = async () => {
    setVerifying(true);
    try {
      const response = await api.post('/tenant/domain/verify');
      if (response.data.verified) {
        toast.success(response.data.message);
      } else {
        toast.info(response.data.message);
      }
      fetchAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify domain');
    } finally {
      setVerifying(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!window.confirm('Are you sure you want to remove this custom domain?')) return;

    setRemoving(true);
    try {
      await api.delete('/tenant/domain');
      toast.success('Custom domain removed');
      fetchAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove domain');
    } finally {
      setRemoving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="text-emerald-500" size={20} />;
      case 'failed':
        return <XCircle className="text-red-500" size={20} />;
      case 'pending':
      default:
        return <Clock className="text-amber-500" size={20} />;
    }
  };

  const getPlanIcon = (plan) => {
    switch (plan) {
      case 'enterprise':
        return <Crown className="text-amber-500" size={20} />;
      case 'reseller':
        return <Building2 className="text-purple-500" size={20} />;
      case 'pro':
        return <Sparkles className="text-indigo-500" size={20} />;
      case 'starter':
        return <Globe className="text-blue-500" size={20} />;
      case 'freemium':
      case 'free':
        return <Globe className="text-emerald-500" size={20} />;
      default:
        return <Globe className="text-slate-400" size={20} />;
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 bg-slate-50 p-8 flex items-center justify-center">
          <RefreshCw className="animate-spin text-indigo-600" size={32} />
        </div>
      </div>
    );
  }

  const currentPlan = planFeatures?.plan_level || 'starter';
  const canUseCustomDomain = planFeatures?.features?.custom_domain || false;

  return (
    <div className="flex">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Globe className="text-indigo-600" size={32} />
              Domain Settings
            </h1>
            <p className="text-slate-500 mt-2">
              Manage your subdomain and custom domain settings
            </p>
          </div>

          {/* Plan Status Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  currentPlan === 'enterprise' ? 'bg-amber-100' :
                  currentPlan === 'reseller' ? 'bg-purple-100' :
                  currentPlan === 'pro' ? 'bg-indigo-100' : 'bg-slate-100'
                }`}>
                  {getPlanIcon(currentPlan)}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Current Plan</p>
                  <p className="text-xl font-semibold capitalize">{currentPlan}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canUseCustomDomain ? (
                  <span className="flex items-center gap-1 text-emerald-600 text-sm bg-emerald-50 px-3 py-1 rounded-full">
                    <Check size={14} />
                    Custom Domain Enabled
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-slate-500 text-sm bg-slate-100 px-3 py-1 rounded-full">
                    <Lock size={14} />
                    Upgrade for Custom Domain
                  </span>
                )}
              </div>
            </div>
          </div>

          <Tabs defaultValue="subdomain" className="space-y-6">
            <TabsList>
              <TabsTrigger value="subdomain" className="gap-2">
                <Globe size={16} />
                Subdomain
              </TabsTrigger>
              <TabsTrigger value="custom-domain" className="gap-2">
                <Sparkles size={16} />
                Custom Domain
                {!canUseCustomDomain && <Lock size={14} className="ml-1 text-slate-400" />}
              </TabsTrigger>
              <TabsTrigger value="plans" className="gap-2">
                <Crown size={16} />
                Plans
              </TabsTrigger>
            </TabsList>

            {/* Subdomain Tab */}
            <TabsContent value="subdomain">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100">
                  <h2 className="text-xl font-semibold">Your Site URL</h2>
                  <p className="text-slate-500 text-sm mt-1">
                    Your public site is accessible at this address. This works on all hosting platforms.
                  </p>
                </div>
                
                <div className="p-6">
                  {!editingSubdomain ? (
                    <div className="space-y-4">
                      {subdomainData?.subdomain ? (
                        <>
                          {/* Primary: Path-based URL */}
                          <div className="flex items-center gap-4 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-sm font-medium text-indigo-700">Primary Site URL</p>
                                <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">Recommended</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <code className="text-lg font-mono text-indigo-600">
                                  {window.location.origin}/site/{subdomainData.subdomain}
                                </code>
                                <SimpleTooltip content="Copy URL">
                                  <button 
                                    onClick={() => copyToClipboard(`${window.location.origin}/site/${subdomainData.subdomain}`)}
                                    className="p-1.5 hover:bg-indigo-100 rounded"
                                  >
                                    <Copy size={16} className="text-indigo-500" />
                                  </button>
                                </SimpleTooltip>
                                <SimpleTooltip content="Open in new tab">
                                  <a 
                                    href={`/site/${subdomainData.subdomain}`}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="p-1.5 hover:bg-indigo-100 rounded"
                                  >
                                    <ExternalLink size={16} className="text-indigo-500" />
                                  </a>
                                </SimpleTooltip>
                              </div>
                              <p className="text-xs text-indigo-600 mt-2">
                                ✓ Works everywhere • No DNS setup needed • Share this URL with your audience
                              </p>
                            </div>
                          </div>
                          
                          {/* Secondary: Subdomain URL (for future/external hosting) */}
                          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-sm text-slate-500">Subdomain URL</p>
                                <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Future Use</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <code className="text-base font-mono text-slate-600">
                                  {subdomainData.full_url}
                                </code>
                                <SimpleTooltip content="Copy URL">
                                  <button 
                                    onClick={() => copyToClipboard(subdomainData.full_url)}
                                    className="p-1.5 hover:bg-slate-200 rounded"
                                  >
                                    <Copy size={16} className="text-slate-400" />
                                  </button>
                                </SimpleTooltip>
                              </div>
                              <p className="text-xs text-slate-500 mt-2">
                                Requires external hosting with wildcard subdomain support
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex justify-end">
                            <Button variant="outline" onClick={() => setEditingSubdomain(true)}>
                              Change Site Slug
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <Globe className="mx-auto text-slate-300 mb-4" size={48} />
                          <p className="text-slate-500 mb-4">No subdomain configured yet</p>
                          <Button onClick={() => setEditingSubdomain(true)}>
                            Set Up Subdomain
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label>Choose your subdomain</Label>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 relative">
                            <Input
                              value={newSubdomain}
                              onChange={(e) => handleSubdomainChange(e.target.value)}
                              placeholder="my-school"
                              className="pr-10"
                            />
                            {checkingSubdomain && (
                              <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" size={16} />
                            )}
                            {!checkingSubdomain && subdomainAvailable === true && (
                              <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500" size={16} />
                            )}
                            {!checkingSubdomain && subdomainAvailable === false && (
                              <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" size={16} />
                            )}
                          </div>
                          <span className="text-slate-500 font-mono">.{subdomainData?.base_domain || 'nexuscms.ai'}</span>
                        </div>
                        {subdomainAvailable === false && (
                          <p className="text-red-500 text-sm mt-1">This subdomain is not available</p>
                        )}
                        {subdomainAvailable === true && (
                          <p className="text-emerald-500 text-sm mt-1">This subdomain is available!</p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          onClick={handleGenerateSubdomain}
                          size="sm"
                        >
                          <Sparkles size={14} className="mr-1" />
                          Suggest
                        </Button>
                        <span className="text-sm text-slate-500">Generate a subdomain from your organization name</span>
                      </div>
                      
                      <div className="flex gap-2 pt-4 border-t">
                        <Button 
                          onClick={handleSaveSubdomain}
                          disabled={savingSubdomain || subdomainAvailable === false || !newSubdomain}
                        >
                          {savingSubdomain ? 'Saving...' : 'Save Subdomain'}
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setEditingSubdomain(false);
                            setNewSubdomain(subdomainData?.subdomain || '');
                            setSubdomainAvailable(null);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Custom Domain Tab */}
            <TabsContent value="custom-domain">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100">
                  <h2 className="text-xl font-semibold">Custom Domain</h2>
                  <p className="text-slate-500 text-sm mt-1">
                    Connect your own domain for a fully branded experience
                  </p>
                </div>
                
                <div className="p-6">
                  {!canUseCustomDomain ? (
                    <div className="text-center py-8 bg-slate-50 rounded-lg">
                      <Lock className="mx-auto text-slate-300 mb-4" size={48} />
                      <h3 className="text-lg font-semibold mb-2">Custom Domains - Pro Feature</h3>
                      <p className="text-slate-500 mb-4 max-w-md mx-auto">
                        Upgrade to Pro, Enterprise, or Reseller plan to connect your own domain 
                        (e.g., yourschool.com)
                      </p>
                      <Button onClick={() => window.location.href = '/settings'}>
                        <Crown size={16} className="mr-2" />
                        Upgrade Plan
                      </Button>
                    </div>
                  ) : domainData?.custom_domain ? (
                    <div className="space-y-6">
                      {/* Current Domain */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          {getStatusIcon(domainData.custom_domain_status)}
                          <div>
                            <code className="text-lg font-mono">{domainData.custom_domain}</code>
                            <p className="text-sm text-slate-500 capitalize mt-1">
                              Status: {domainData.custom_domain_status || 'pending'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {domainData.custom_domain_status !== 'verified' && (
                            <Button 
                              variant="outline" 
                              onClick={handleVerifyDomain}
                              disabled={verifying}
                            >
                              <RefreshCw size={16} className={`mr-2 ${verifying ? 'animate-spin' : ''}`} />
                              Verify
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            onClick={handleRemoveDomain}
                            disabled={removing}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 size={16} className="mr-2" />
                            Remove
                          </Button>
                        </div>
                      </div>
                      
                      {/* DNS Instructions */}
                      {domainData.custom_domain_status !== 'verified' && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
                            <div>
                              <h4 className="font-medium text-amber-800">DNS Configuration Required</h4>
                              <p className="text-sm text-amber-700 mt-1 mb-3">
                                Add the following DNS record to your domain provider:
                              </p>
                              <div className="bg-white rounded p-3 space-y-2 text-sm font-mono">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Type:</span>
                                  <span className="font-semibold">{domainData.dns_instructions?.type}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Name:</span>
                                  <span className="font-semibold">{domainData.dns_instructions?.name}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Target:</span>
                                  <span className="font-semibold">{domainData.dns_instructions?.target}</span>
                                </div>
                              </div>
                              <p className="text-xs text-amber-600 mt-2">
                                {domainData.dns_instructions?.note}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Globe className="mx-auto text-slate-300 mb-4" size={48} />
                      <p className="text-slate-500 mb-4">No custom domain configured</p>
                      <Button onClick={() => setShowAddDomain(true)}>
                        <Globe size={16} className="mr-2" />
                        Add Custom Domain
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Plans Tab */}
            <TabsContent value="plans">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100">
                  <h2 className="text-xl font-semibold">Available Plans</h2>
                  <p className="text-slate-500 text-sm mt-1">
                    Compare features across different plan tiers
                  </p>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                    {planFeatures?.all_plans && Object.entries(planFeatures.all_plans)
                      .filter(([planName]) => planName !== 'free') // Filter out legacy 'free'
                      .map(([planName, features]) => (
                      <div 
                        key={planName}
                        className={`border rounded-xl p-4 ${
                          currentPlan === planName || (currentPlan === 'free' && planName === 'freemium')
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-3">
                          {getPlanIcon(planName)}
                          <h3 className="font-semibold capitalize text-sm">
                            {features.display_name || planName}
                          </h3>
                        </div>
                        {(currentPlan === planName || (currentPlan === 'free' && planName === 'freemium')) && (
                          <span className="inline-block text-xs bg-indigo-600 text-white px-2 py-0.5 rounded-full mb-3">
                            Current
                          </span>
                        )}
                        {planName === 'freemium' && (
                          <p className="text-xs text-emerald-600 font-medium mb-2">Free Forever</p>
                        )}
                        {features.price_monthly > 0 && (
                          <p className="text-lg font-bold mb-2">${features.price_monthly}<span className="text-xs text-slate-500">/mo</span></p>
                        )}
                        
                        <ul className="space-y-1.5 text-xs">
                          <li className="flex items-center gap-1.5">
                            {features.subdomain ? (
                              <Check size={12} className="text-emerald-500" />
                            ) : (
                              <XCircle size={12} className="text-slate-300" />
                            )}
                            <span>Subdomain</span>
                          </li>
                          <li className="flex items-center gap-1.5">
                            {features.custom_domain ? (
                              <Check size={12} className="text-emerald-500" />
                            ) : (
                              <XCircle size={12} className="text-slate-300" />
                            )}
                            <span>Custom Domain</span>
                          </li>
                          <li className="flex items-center gap-1.5">
                            {!features.forced_branding && features.remove_branding ? (
                              <Check size={12} className="text-emerald-500" />
                            ) : (
                              <XCircle size={12} className="text-slate-300" />
                            )}
                            <span>Remove Branding</span>
                          </li>
                          <li className="text-slate-500">
                            {features.max_pages === -1 ? '∞' : features.max_pages === 0 ? 'Landing only' : features.max_pages} pages
                          </li>
                          <li className="text-slate-500">
                            {features.max_forms === -1 ? '∞' : features.max_forms} forms
                          </li>
                          <li className="text-slate-500">
                            {features.max_assets === -1 ? '∞' : features.max_assets} assets
                          </li>
                          <li className="text-slate-500">
                            {features.max_team_members === -1 ? '∞' : features.max_team_members === 0 ? '1 admin' : `+${features.max_team_members}`} team
                          </li>
                          {features.max_newsletters !== undefined && (
                            <li className="flex items-center gap-1.5">
                              {features.max_newsletters !== 0 ? (
                                <Check size={12} className="text-emerald-500" />
                              ) : (
                                <XCircle size={12} className="text-slate-300" />
                              )}
                              <span>Newsletters</span>
                            </li>
                          )}
                          {features.analytics && (
                            <li className="flex items-center gap-1.5">
                              <Check size={12} className="text-emerald-500" />
                              <span>Analytics</span>
                            </li>
                          )}
                          {features.ai_features && (
                            <li className="flex items-center gap-1.5 text-purple-600">
                              <Check size={12} />
                              <span>AI Features</span>
                            </li>
                          )}
                          {features.api_access && (
                            <li className="flex items-center gap-1.5 text-purple-600">
                              <Check size={12} />
                              <span>API Access</span>
                            </li>
                          )}
                          {features.sso && (
                            <li className="flex items-center gap-1.5 text-purple-600">
                              <Check size={12} />
                              <span>SSO</span>
                            </li>
                          )}
                          {features.white_label && (
                            <li className="flex items-center gap-1.5 text-purple-600">
                              <Check size={12} />
                              <span>White Label</span>
                            </li>
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 text-center">
                    <Button onClick={() => window.location.href = '/settings'}>
                      Manage Subscription
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Add Custom Domain Dialog */}
      <Dialog open={showAddDomain} onOpenChange={setShowAddDomain}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Domain</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Domain</Label>
              <Input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="yourschool.com"
                className="mt-2"
              />
              <p className="text-sm text-slate-500 mt-2">
                Enter your root domain only — no <code>www.</code>, no <code>http://</code> or <code>https://</code> (e.g. <strong>yourschool.com</strong>). The <code>www.</code> version is handled automatically.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDomain(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddDomain} disabled={adding}>
              {adding ? 'Adding...' : 'Add Domain'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DomainSettings;
